import { z } from 'zod';
import {
  ActorKind,
  ActorRef,
  Email,
  Id,
  JsonObject,
  MemberStatus,
  PermissionBit,
  PermissionMask,
  Timestamp,
  atLeastOneField,
  cursorPage,
} from './common.js';
import { User } from './users.js';

export const Role = z.object({
  id: Id,
  workspace_id: Id.nullable().optional(),
  name: z.string().max(60),
  permission_mask: PermissionMask,
  bits: z.array(PermissionBit).optional(),
  is_system: z.boolean(),
  member_count: z.int().optional(),
  /** Release 3 DEL-2: the per-workspace guest role (mask 0; 409 `guest_only_role` on edit/delete). */
  is_guest: z.boolean().optional(),
});
export type Role = z.infer<typeof Role>;

export const RoleCreate = z.object({
  name: z.string().min(1).max(60),
  permission_mask: PermissionMask,
});
export type RoleCreate = z.infer<typeof RoleCreate>;

export const RoleUpdate = atLeastOneField(
  z.object({
    name: z.string().min(1).max(60).optional(),
    permission_mask: PermissionMask.optional(),
  }),
);
export type RoleUpdate = z.infer<typeof RoleUpdate>;

export const Member = z.object({
  id: Id,
  workspace_id: Id,
  user: User,
  role: Role,
  status: MemberStatus,
  is_owner: z.boolean().optional(),
  invited_by: Id.nullable().optional(),
  joined_at: Timestamp.nullable(),
  /** Release 3 DEL-2: holds the guest role; sees only boards with an override. */
  is_guest: z.boolean().optional(),
});
export type Member = z.infer<typeof Member>;

export const MemberPage = cursorPage(Member);
export type MemberPage = z.infer<typeof MemberPage>;

export const MemberInvite = z.object({
  email: Email,
  role_id: Id,
  message: z.string().max(1000).optional(),
});
export type MemberInvite = z.infer<typeof MemberInvite>;

export const MemberUpdate = atLeastOneField(
  z.object({
    role_id: Id.optional(),
    status: z.enum(['active', 'suspended']).optional(),
  }),
);
export type MemberUpdate = z.infer<typeof MemberUpdate>;

export const BoardMemberOverride = z.object({
  board_id: Id,
  member_id: Id,
  permission_mask: PermissionMask,
  granted_by: Id,
  created_at: Timestamp,
});
export type BoardMemberOverride = z.infer<typeof BoardMemberOverride>;

/** Inline body of `PUT /boards/{id}/overrides/{memberId}` (not a named component). */
export const BoardMemberOverrideSet = z.object({ permission_mask: PermissionMask });
export type BoardMemberOverrideSet = z.infer<typeof BoardMemberOverrideSet>;

export const EffectivePermissionSource = z.enum([
  'role',
  'override',
  'ancestor_override',
  'share_link',
  'guest_upload',
  'personal_access_token',
]);
export type EffectivePermissionSource = z.infer<typeof EffectivePermissionSource>;

export const EffectivePermission = z.object({
  board_id: Id,
  effective_mask: PermissionMask,
  bits: z.array(PermissionBit).optional(),
  source: EffectivePermissionSource,
  override_board_id: Id.nullable().optional(),
});
export type EffectivePermission = z.infer<typeof EffectivePermission>;

export const AuditTargetType = z.enum([
  'workspace',
  'member',
  'role',
  'library',
  'board',
  'asset',
  'version',
  'comment',
  'share_link',
  'guest_upload_form',
  'custom_field',
  'tag',
  'user',
]);
export type AuditTargetType = z.infer<typeof AuditTargetType>;

export const AuditEvent = z.object({
  id: Id,
  workspace_id: Id,
  actor_member_id: Id.nullable().optional(),
  actor_kind: ActorKind,
  actor: ActorRef.optional(),
  action: z.string(),
  target_type: AuditTargetType,
  target_id: Id,
  metadata: JsonObject.optional(),
  ip: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
  created_at: Timestamp,
});
export type AuditEvent = z.infer<typeof AuditEvent>;

export const AuditEventPage = cursorPage(AuditEvent);
export type AuditEventPage = z.infer<typeof AuditEventPage>;

export const AuditExportRequest = z.object({
  actor_member_id: Id.optional(),
  action: z.string().optional(),
  target_id: Id.optional(),
  from: Timestamp.optional(),
  to: Timestamp.optional(),
});
export type AuditExportRequest = z.infer<typeof AuditExportRequest>;
